import * as React from 'react';

import { cn } from '../lib/utils';
import { ErrorState } from './error-state';
import { EmptyState } from './empty-state';

/**
 * A single render-state envelope. Standardises the six recurring
 * states every Tyndo view needs to handle:
 *
 *   1. loading         — initial fetch
 *   2. error           — network or 5xx failure
 *   3. permissionDenied — 403/401 surfaced as friendly copy
 *   4. validation      — 400/422 with field-level messages
 *   5. optimistic      — write is pending; show inline indicator
 *   6. empty           — no rows
 *
 * Consumers wrap their content with `<ViewState ...>{content}</ViewState>`
 * and the wrapper short-circuits to the matching placeholder when any
 * non-success state is set. Defaults are sensible — pass only the
 * dimensions that apply.
 */
export interface ViewStateProps {
  loading?: boolean;
  error?: { message: string; retry?: () => void } | null;
  permissionDenied?: boolean;
  /** Aggregate validation message + optional per-field issues. */
  validation?: { message: string; fields?: Record<string, string> } | null;
  /** True while an optimistic write is pending — overlays without hiding content. */
  optimistic?: boolean;
  /** True when the view loaded successfully but has zero rows. */
  empty?: boolean;
  /** Empty-state copy. */
  emptyTitle?: string;
  emptyDescription?: string;
  emptyAction?: React.ReactNode;
  /** Number of skeleton rows to render in the loading state. */
  loadingRows?: number;
  /** The success content. */
  children: React.ReactNode;
  className?: string;
}

export function ViewState({
  loading,
  error,
  permissionDenied,
  validation,
  optimistic,
  empty,
  emptyTitle = 'No data',
  emptyDescription = 'There is nothing to show here yet.',
  emptyAction,
  loadingRows = 6,
  children,
  className,
}: ViewStateProps) {
  if (loading) {
    return (
      <div
        className={cn('space-y-2', className)}
        role="status"
        aria-busy="true"
        aria-label="Loading"
        data-testid="view-state-loading"
      >
        {Array.from({ length: loadingRows }).map((_, i) => (
          <div
            key={i}
            className="h-10 rounded-md border border-border/40 bg-muted/30 animate-pulse"
          />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className={className} data-testid="view-state-error">
        <ErrorState
          title="Something went wrong"
          message={error.message}
          onRetry={error.retry}
        />
      </div>
    );
  }

  if (permissionDenied) {
    return (
      <div className={className} data-testid="view-state-permission-denied">
        <ErrorState
          title="Permission denied"
          message="Your role does not have access to this view. Contact your administrator if you believe this is a mistake."
        />
      </div>
    );
  }

  if (validation) {
    return (
      <div
        className={cn('rounded-md border border-destructive/60 bg-destructive/10 p-3 text-sm', className)}
        role="alert"
        data-testid="view-state-validation"
      >
        <p className="font-medium text-destructive">{validation.message}</p>
        {validation.fields && Object.keys(validation.fields).length > 0 && (
          <ul className="mt-1 list-inside list-disc text-xs text-destructive/90">
            {Object.entries(validation.fields).map(([field, msg]) => (
              <li key={field}>
                <span className="font-mono">{field}</span>: {msg}
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  if (empty) {
    return (
      <div className={className} data-testid="view-state-empty">
        <EmptyState title={emptyTitle} description={emptyDescription} action={emptyAction} />
      </div>
    );
  }

  return (
    <div className={cn('relative', className)} data-testid="view-state-content">
      {optimistic && (
        <div
          className="absolute right-2 top-2 z-10 rounded-md bg-primary/10 px-2 py-1 text-xs text-primary"
          role="status"
          aria-label="Saving"
          data-testid="view-state-optimistic"
        >
          Saving…
        </div>
      )}
      {children}
    </div>
  );
}

ViewState.displayName = 'ViewState';

/**
 * Convenience hook: project a useApiQuery / useApiMutation result into a
 * ViewState-compatible shape. Returns the props ready to spread.
 */
export function useViewState(opts: {
  isLoading?: boolean;
  isError?: boolean;
  errorMessage?: string;
  retry?: () => void;
  count?: number;
  optimistic?: boolean;
}): Partial<ViewStateProps> {
  return {
    loading: opts.isLoading,
    error:
      opts.isError && opts.errorMessage
        ? { message: opts.errorMessage, retry: opts.retry }
        : null,
    empty: !opts.isLoading && !opts.isError && (opts.count ?? 0) === 0,
    optimistic: opts.optimistic,
  };
}
